import {
  type CSSProperties,
  type KeyboardEvent,
  type ReactNode,
  type Ref,
  useEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';

import { cn } from '@shared/lib/cn';
import { PortalContainerContext } from '@shared/lib/portal';

import {
  MODAL_LAYER_PANEL_CLASS,
  MODAL_LAYER_WRAPPER_CLASS,
  MODAL_LAYER_Z_CLASS,
  type ModalLayerPlacement,
  type ModalLayerZ,
} from './dialog.constant';

export interface ModalLayerProps {
  open: boolean;
  /** Esc · 배경을 눌러 닫으려 할 때 */
  onDismiss: () => void;
  /** 화면 가운데(Dialog) · 오른쪽 · 왼쪽 · 아래(Drawer) */
  placement: ModalLayerPlacement;
  /** z-index 단계. Dialog = modal, Drawer = overlay(Drawer 위에 Dialog 가 뜬다) */
  layer: ModalLayerZ;
  role?: 'dialog' | 'alertdialog';
  /** 제목 요소 id(`aria-labelledby`) */
  labelledBy?: string;
  /** 설명 요소 id(`aria-describedby`) */
  describedBy?: string;
  /** 배경(덮개)을 누르면 닫는다(덮개에 손가락 커서). 기본 true — 팝업 · 다이얼로그 공통(2026-10-07 사용자) */
  closeOnBackdrop?: boolean;
  /** Esc 로 닫는다. 기본 true */
  closeOnEscape?: boolean;
  /** 불러오는 중(`aria-busy`) */
  busy?: boolean;
  /** portal · 덮개 · 포커스 가두기 없이 제자리에 그린다(fe-rules 3절, 확인 페이지) */
  inline?: boolean;
  /** 판(panel)의 모양 */
  className?: string;
  style?: CSSProperties;
  ref?: Ref<HTMLDivElement>;
  children: ReactNode;
  [dataAttribute: `data-${string}`]: string | undefined;
}

const FOCUSABLE =
  'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

function focusables(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
    (element) => !element.closest('[inert]') && element.getAttribute('aria-hidden') !== 'true',
  );
}

/** 여러 겹이 떠도 body 스크롤은 한 번만 잠그고 마지막 것이 닫힐 때 푼다 */
let lockCount = 0;
let previousOverflow = '';
function lockScroll() {
  if (lockCount === 0) {
    previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
  }
  lockCount += 1;
}
function unlockScroll() {
  lockCount = Math.max(0, lockCount - 1);
  if (lockCount === 0) document.body.style.overflow = previousOverflow;
}

/**
 * 겹쳐 뜨는 창(Dialog · AlertDialog · Drawer)의 공통 틀.
 * - portal(기본): `document.body` 에 덮개(`bg-bg-overlay`) + 판을 띄운다. 열리면 판 안의 첫 요소로 포커스를 옮기고
 *   Tab 을 판 안에 가두며, 닫히면 연 요소로 포커스를 돌려준다. body 스크롤을 잠근다.
 * - inline: 판만 제자리에 그린다(`aria-modal` · 덮개 · 포커스 가두기 없음).
 * - 판 안의 떠 있는 목록 · 달력 · 메뉴(`usePortalContainer`)는 덮개 틀에 붙는다 — 판(z-modal) 뒤로 숨거나
 *   본문 스크롤 영역에 잘리지 않는다.
 * - Esc 는 가장 안쪽 창만 닫는다(처리하면 전파를 멈춘다).
 * 명령형으로 띄울 때는 `overlay.open(({ close }) => <Dialog open onOpenChange={(o) => !o && close()} … />)`.
 */
export function ModalLayer({
  open,
  onDismiss,
  placement,
  layer,
  role = 'dialog',
  labelledBy,
  describedBy,
  closeOnBackdrop = true,
  closeOnEscape = true,
  busy = false,
  inline = false,
  className,
  style,
  ref,
  children,
  ...data
}: ModalLayerProps) {
  const panelRef = useRef<HTMLDivElement | null>(null);
  /** 덮개 틀. 안쪽의 목록 · 달력 · 메뉴는 이 틀에 portal 로 붙는다(창 뒤로 숨거나 본문 스크롤에 잘리지 않게) */
  const [layerEl, setLayerEl] = useState<HTMLDivElement | null>(null);
  const modal = open && !inline;

  useEffect(() => {
    const panel = panelRef.current;
    if (!modal || !panel) return;
    const previous = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    (focusables(panel)[0] ?? panel).focus();
    lockScroll();
    return () => {
      unlockScroll();
      if (previous?.isConnected) previous.focus();
    };
  }, [modal]);

  if (!open) return null;

  function handleKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (event.key === 'Escape' && closeOnEscape) {
      event.stopPropagation();
      onDismiss();
      return;
    }
    const panel = panelRef.current;
    if (!modal || event.key !== 'Tab' || !panel) return;
    // 판 + 덮개 틀에 붙은 목록 · 달력(판 뒤 DOM 순서)까지 가둔다
    const list = focusables(layerEl ?? panel);
    const first = list[0];
    const last = list[list.length - 1];
    if (!first || !last) {
      event.preventDefault();
      return;
    }
    if (event.shiftKey && (document.activeElement === first || document.activeElement === panel)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  const panel = (
    <div
      ref={(node) => {
        panelRef.current = node;
        if (typeof ref === 'function') ref(node);
        else if (ref) ref.current = node;
      }}
      role={role}
      aria-modal={modal || undefined}
      aria-labelledby={labelledBy}
      aria-describedby={describedBy}
      aria-busy={busy || undefined}
      tabIndex={-1}
      onKeyDown={handleKeyDown}
      {...data}
      style={style}
      // 배치(위치 · 스크롤) 클래스는 패널 모양(className)보다 우선한다 — cn 은 겹치면 뒤의 것을 남긴다
      className={cn('outline-hidden', className, !inline && MODAL_LAYER_PANEL_CLASS[placement])}
    >
      {children}
    </div>
  );

  if (inline) return panel;

  return createPortal(
    <div
      ref={setLayerEl}
      data-slot="modal-layer"
      className={cn(
        'fixed inset-0',
        MODAL_LAYER_Z_CLASS[layer],
        MODAL_LAYER_WRAPPER_CLASS[placement],
      )}
    >
      <div
        data-slot="backdrop"
        aria-hidden="true"
        className={cn('absolute inset-0 bg-bg-overlay', closeOnBackdrop && 'cursor-pointer')}
        onMouseDown={(event) => event.preventDefault()}
        onClick={() => {
          if (closeOnBackdrop) onDismiss();
        }}
      />
      <PortalContainerContext value={layerEl}>{panel}</PortalContainerContext>
    </div>,
    document.body,
  );
}
